import type {
  HazardIdentification,
  MachineLocation,
} from '@/shared/hooks/api/machineryDirectiveCompliance'

export const MACHINE_LOCATION_SOURCE_SYSTEM = 'SYSTEM' as const
export const MACHINE_LOCATION_SOURCE_AGENT = 'AGENT' as const

export function isAgentMachineLocation(location: {
  source: string | null
}): boolean {
  return location.source === MACHINE_LOCATION_SOURCE_AGENT
}

export const WHOLE_MACHINE_GENERIC_LOCATION = {
  name: 'Whole Machine/Generic',
  description:
    'Default location for hazards that apply to the whole machine or are not specific to a part of the machine.',
  source: MACHINE_LOCATION_SOURCE_SYSTEM,
} as const

export type MachineLocationRow = MachineLocation & {
  linkedHazards: HazardIdentification[]
}

export function isSystemMachineLocation(location: {
  source: string | null
}): boolean {
  return location.source === MACHINE_LOCATION_SOURCE_SYSTEM
}

export function getMachineLocationDisplayName(
  location: Pick<MachineLocation, 'name' | 'source'>,
  translate: (key: string) => string
): string {
  if (isSystemMachineLocation(location)) {
    return translate('systemMachineLocation.name')
  }
  return location.name
}

export function getMachineLocationDisplayDescription(
  location: Pick<MachineLocation, 'description' | 'source'>,
  translate: (key: string) => string
): string {
  if (isSystemMachineLocation(location)) {
    return translate('systemMachineLocation.description')
  }
  return location.description
}

export function canEditMachineLocation(location: {
  source: string | null
}): boolean {
  return !isSystemMachineLocation(location)
}

export function canDeleteMachineLocation(
  location: {
    source: string | null
    linkedHazards?: HazardIdentification[]
  }
): boolean {
  const linkedHazardCount = location.linkedHazards?.length ?? 0
  return canEditMachineLocation(location) && linkedHazardCount === 0
}

export function compareMachineLocationsForList(
  left: { source: string | null; createdAt: string },
  right: { source: string | null; createdAt: string }
): number {
  const leftIsSystem = isSystemMachineLocation(left)
  const rightIsSystem = isSystemMachineLocation(right)
  if (leftIsSystem && !rightIsSystem) {
    return -1
  }
  if (!leftIsSystem && rightIsSystem) {
    return 1
  }
  return (
    new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime()
  )
}

export function sortMachineLocationsForList<
  T extends { source: string | null; createdAt: string },
>(locations: T[]): T[] {
  return [...locations].sort(compareMachineLocationsForList)
}

export function buildMachineLocationRows(
  locations: MachineLocation[],
  hazards: HazardIdentification[]
): MachineLocationRow[] {
  const hazardsByLocation = new Map<string, HazardIdentification[]>()
  for (const hazard of hazards) {
    const locationId = hazard.machineLocationId
    if (!locationId) {
      continue
    }
    const existing = hazardsByLocation.get(locationId)
    if (existing) {
      existing.push(hazard)
    } else {
      hazardsByLocation.set(locationId, [hazard])
    }
  }

  return sortMachineLocationsForList(locations).map((location) => ({
    ...location,
    linkedHazards: hazardsByLocation.get(location.id) ?? [],
  }))
}

export function getHazardDisplayName(
  hazard: HazardIdentification,
  translate: (key: string) => string,
  naLabel: string
): string {
  const description = hazard.description?.trim()
  if (description != null && description.length > 0) {
    return description
  }

  const type = hazard.type?.trim()
  if (type != null && type.length > 0) {
    const translationKey = `hazardsPage.enums.hazardTypes.${type}`
    const translated = translate(translationKey)
    const typeLabel = translated === translationKey ? type : translated
    return `${typeLabel} (#${hazard.id})`
  }

  return naLabel
}

export function formatEvidence(reasoning: string | null | undefined): string {
  const trimmed = reasoning?.trim()
  if (trimmed == null || trimmed.length === 0) {
    return '-'
  }
  return trimmed
}
